// ── PARENT: CLASSES (Tap/Voice registration + one-time Classes & Lessons) ──
// Two distinct parent-facing flows, kept together in one module because they
// share the "Classes" nav item and the p-classes/p-tapvoice views were
// adjacent in legacy-monolith.js:
//  - p-tapvoice/p-tapvoice-done: Tap/Voice registration. Deliberately
//    separate from p-reenroll — open to any existing family (no
//    PC-enrollment requirement), with its own window. Nothing is charged at
//    registration; Classes bill monthly like PC/BB once active months arrive.
//    A card on file is still required before Register is enabled (via the
//    shared cardModal from billing-shared.js) — TapVoiceRoutes::
//    registerPerformers() enforces this server-side too, so the September
//    billing run doesn't silently fail for a family with no card.
//  - p-classes: one-time, self-serve à la carte classes/lessons, charged in
//    full at checkout — unrelated billing model, same "Classes" umbrella.
//
// esc (shared classic script) stays a bare global — not migrated yet.
// Wired via VIEW_INIT (phase 2 of the onclick → addEventListener migration)
// instead of window/onclick. processClassCheckout is attached directly
// inline inside checkoutClasses()'s own modal (imperative function with
// control over the DOM it just wrote via openModal).
import { DB, session, PROGRAMS } from '../core/state.js';
import { apiFetch } from '../core/api.js';
import { calcAge } from '../core/format.js';
import { kidPrograms, currentSeasonYear } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh } from '../core/routing.js';
import { tvWindowOpen, tvEligiblePrograms, tvIsFull, tvMonthlyLabel } from './catalog-shared.js';
import { programOneTimePrice, showChargeError, cardModal } from './billing-shared.js';

// ── Tap/Voice registration ────────────────────────────────────────────
let tvSelections = {}; // performerId -> { programId: true }
function tvToggle(performerId, programId) {
  tvSelections[performerId] = tvSelections[performerId] || {};
  if (tvSelections[performerId][programId]) delete tvSelections[performerId][programId];
  else tvSelections[performerId][programId] = true;
  refresh();
}
VIEWS['p-tapvoice'] = () => {
  if (!tvWindowOpen()) {
    return `<div class="page-head"><span class="eyebrow" style="cursor:pointer" onclick="route('p-home')">‹ Dashboard</span><h2 class="serif">Classes</h2></div>
      <div class="card"><p class="empty">Class registration is not currently open.</p></div>`;
  }
  const kids = DB.children.filter(c => c.accountId === session.id);
  if (!kids.length) {
    return `<div class="page-head"><span class="eyebrow" style="cursor:pointer" onclick="route('p-home')">‹ Dashboard</span><h2 class="serif">Classes</h2></div>
      <div class="card"><p class="empty">No performers on this account yet.</p></div>`;
  }
  const anySelected = kids.some(k => tvSelections[k.id] && Object.keys(tvSelections[k.id]).length);
  return `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('p-home')">‹ Dashboard</span>
      <h2 class="serif">Classes <em>Registration</em></h2>
    </div>
    <p style="font-size:13px;color:var(--muted);margin-bottom:16px;max-width:560px">Nothing is charged today. Classes bill monthly, same as Performance Company/Broadway Bound, to your card on file — see each class's active months below.</p>
    <div class="card" style="max-width:640px">
      ${kids.map(k => {
        const eligible = tvEligiblePrograms(k);
        if (!eligible.length) return '';
        const sel = tvSelections[k.id] || {};
        return `
        <div style="padding:16px 0;border-bottom:1px solid var(--border-soft)">
          <div style="font-size:15px;color:var(--cream);margin-bottom:8px">${esc(k.name)} <span style="font-size:12px;color:var(--muted)">(age ${calcAge(k.dob)})</span></div>
          ${eligible.map(p => {
            const full = tvIsFull(p);
            const checked = !!sel[p.id];
            const d = p.details || {};
            const metaParts = [d.dates, d.time, d.teacher].filter(Boolean);
            return `<div style="padding:7px 10px;border-radius:6px;border:1px solid ${checked ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${checked ? 'rgba(201,168,76,0.06)' : 'transparent'};margin-bottom:6px">
              <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
                <input type="checkbox" ${checked ? 'checked' : ''} data-action="tv-toggle" data-kid="${k.id}" data-program="${p.id}" style="width:auto;accent-color:var(--gold)">
                <span style="flex:1;font-size:13px;color:var(--cream)">${esc(p.name)}${full ? ' <span style=\"color:var(--gold-light);font-size:11px\">(waitlist — full)</span>' : ''}</span>
                <span style="font-size:12px;color:var(--gold-light)">${tvMonthlyLabel(p)}</span>
              </label>
              ${metaParts.length ? `<div style="font-size:11px;color:var(--muted);margin-top:5px;padding-left:26px">${metaParts.map(esc).join(' · ')}</div>` : ''}
              ${d.description ? `<div style="font-size:12px;color:var(--muted);margin-top:4px;padding-left:26px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">${esc(d.description)}</div>
              <button data-action="tv-detail" data-program="${p.id}" style="margin-left:26px;margin-top:2px;color:var(--gold);background:none;border:none;cursor:pointer;font-size:11px;padding:0;font-family:inherit">More &rsaquo;</button>` : ''}
              ${d.note ? `<div style="font-size:11px;color:var(--gold-light);font-style:italic;margin-top:4px;padding-left:26px">${esc(d.note)}</div>` : ''}
            </div>`;
          }).join('')}
        </div>`;
      }).join('') || '<p class="empty">No Tap or Voice offerings are available for your performers right now.</p>'}
      ${session.card
        ? `<button class="btn" style="margin-top:14px" data-action="submit-tapvoice" ${anySelected ? '' : 'disabled'}>Register</button>`
        : `<div style="font-size:12px;color:var(--red);margin:14px 0 8px">No card on file. A card is required before you can register — nothing is charged today; it's only used for monthly billing once classes start.</div>
           <button class="btn" style="width:100%" data-action="add-card-tapvoice">Add a card</button>`}
    </div>`;
};

VIEW_INIT['p-tapvoice'] = () => {
  document.querySelectorAll('[data-action="tv-toggle"]').forEach(cb => {
    cb.addEventListener('change', () => tvToggle(cb.dataset.kid, cb.dataset.program));
  });
  document.querySelectorAll('[data-action="submit-tapvoice"]').forEach(btn => {
    btn.addEventListener('click', submitTapVoice);
  });
  document.querySelectorAll('[data-action="add-card-tapvoice"]').forEach(btn => {
    btn.addEventListener('click', cardModal);
  });
  document.querySelectorAll('[data-action="tv-detail"]').forEach(btn => {
    btn.addEventListener('click', () => tvDetail(btn.dataset.program));
  });
};
// Mirrors catalog.html's openDetail() -- same "meta rows + full
// description + note" detail view, just through the SPA's own modal
// (openModal/closeModal) instead of that page's bespoke overlay markup.
function tvDetail(programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  if (!p) return;
  const d = p.details || {};
  const metaParts = [d.dates, d.time, d.teacher].filter(Boolean);
  openModal(`
    <h3>${esc(p.name)}</h3>
    ${metaParts.length ? `<p style="font-size:13px;color:var(--muted);margin-bottom:12px">${metaParts.map(esc).join(' · ')}</p>` : ''}
    ${d.description ? `<p style="font-size:14px;color:var(--cream);line-height:1.6;margin-bottom:${d.note ? '10px' : '0'}">${esc(d.description)}</p>` : ''}
    ${d.note ? `<p style="font-size:12px;color:var(--gold-light);font-style:italic;margin:0">${esc(d.note)}</p>` : ''}
  `);
}
async function submitTapVoice() {
  const kids = DB.children.filter(c => c.accountId === session.id);
  const selections = [];
  kids.forEach(k => {
    Object.keys(tvSelections[k.id] || {}).forEach(programId => {
      selections.push({ performerId: k.id, programId });
    });
  });
  if (!selections.length) return;
  const btn = document.querySelector('#view .btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Registering…'; }
  try {
    await apiFetch('/tap-voice/register', {
      method: 'POST', body: JSON.stringify({ selections, seasonYear: DB.settings.upcomingSeasonYear })
    });
    DB.seasonEnrollments = (await apiFetch('/enrollments')).map(se => ({
      id: se.id, performerId: se.performerId, programId: se.programId,
      seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
      rateOverride: se.rateOverride ?? null,
      registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
    }));
    tvSelections = {};
    route('p-tapvoice-done');
  } catch (e) {
    toastError(e.message);
    if (btn) { btn.disabled = false; btn.textContent = 'Register'; }
  }
}
VIEWS['p-tapvoice-done'] = () => `
  <div class="page-head"><h2 class="serif">Classes <em>Registered</em></h2></div>
  <div class="card" style="max-width:520px">
    <div style="text-align:center;padding:20px 0">
      <div style="font-size:40px;color:var(--gold);margin-bottom:14px">✦</div>
      <div style="font-family:'Libre Baskerville',serif;font-size:20px;color:var(--cream);margin-bottom:10px">You're registered</div>
      <p style="font-size:14px;color:var(--muted);line-height:1.7;max-width:380px;margin:0 auto">Nothing was charged today. Classes bill monthly to your card on file, same as Performance Company/Broadway Bound, once each class's active months arrive.</p>
      <button class="btn" style="width:auto;margin-top:20px;padding:11px 24px" onclick="route('p-home')">Back to dashboard</button>
    </div>
  </div>`;

// ── Classes & Lessons (one-time, self-serve) ──────────────────────────
// cart: { [childId]: Set-of-classIds } held in a plain object of arrays
let classCart = {};

function oneTimeClasses() {
  return PROGRAMS.filter(p => p.active && p.billingType === 'onetime');
}
function kidEligibleForClass(k, p) {
  const age = calcAge(k.dob);
  if (p.ageMin != null && age < p.ageMin) return false;
  if (p.ageMax != null && age > p.ageMax) return false;
  return true;
}
function cartHas(childId, classId) { return (classCart[childId] || []).includes(classId); }
function cartToggle(childId, classId) {
  const cur = classCart[childId] || [];
  classCart[childId] = cur.includes(classId) ? cur.filter(id => id !== classId) : [...cur, classId];
  refresh();
}
function cartTotal() {
  let t = 0;
  Object.entries(classCart).forEach(([childId, ids]) => ids.forEach(cid => {
    const p = PROGRAMS.find(x => x.id === cid); if (p) t += programOneTimePrice(p);
  }));
  return t;
}
function cartCount() { return Object.values(classCart).reduce((s, ids) => s + ids.length, 0); }

VIEWS['p-classes'] = () => {
  classCart = classCart || {};
  const kids = DB.children.filter(c => c.accountId === session.id);
  const classes = oneTimeClasses();
  const card = session.card ? `${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : null;
  const total = cartTotal();

  if (!kids.length) {
    return `<div class="page-head"><h2 class="serif">Classes &amp; <em>Lessons</em></h2></div>
      <div class="card"><p class="empty">No performers on your account yet. Contact CSYP to get set up.</p></div>`;
  }

  return `
    <div class="page-head">
      <h2 class="serif">Classes &amp; <em>Lessons</em></h2>
      <p class="sub">Add à la carte classes and lessons for your performers. These are billed once, in full, to your card on file — separate from program tuition.</p>
    </div>
    <div class="grid-2" style="align-items:start">
      <div>
        ${kids.map(k => {
          const eligible = classes.filter(p => kidEligibleForClass(k, p));
          const alreadyIn = kidPrograms(k);
          return `
          <div class="card" style="margin-bottom:16px">
            <div class="card-hd">${esc(k.name)} <span class="sub" style="font-weight:400">age ${calcAge(k.dob)}</span></div>
            ${eligible.length ? eligible.map(p => {
              const enrolled = alreadyIn.includes(p.id);
              const inCart = cartHas(k.id, p.id);
              return `
              <label style="display:flex;align-items:flex-start;gap:11px;padding:12px;border-radius:8px;border:1px solid ${inCart ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${inCart ? 'rgba(201,168,76,0.06)' : 'transparent'};margin-bottom:8px;cursor:${enrolled ? 'default' : 'pointer'};opacity:${enrolled ? '0.6' : '1'}">
                <input type="checkbox" ${inCart ? 'checked' : ''} ${enrolled ? 'disabled' : ''} data-action="cart-toggle" data-kid="${k.id}" data-program="${p.id}" style="width:auto;margin-top:2px;accent-color:var(--gold)">
                <div style="flex:1;min-width:0">
                  <div style="display:flex;justify-content:space-between;gap:10px">
                    <span style="font-size:14px;color:var(--cream)">${esc(p.name)}</span>
                    <span style="font-size:14px;color:var(--gold-light);flex-shrink:0">${fmt$(programOneTimePrice(p))}</span>
                  </div>
                  ${p.description ? `<div style="font-size:12px;color:var(--muted);margin-top:3px">${esc(p.description)}</div>` : ''}
                  ${enrolled ? `<div style="font-size:11px;color:var(--gold);margin-top:4px">Already enrolled</div>` : ''}
                </div>
              </label>`;
            }).join('') : `<p class="empty" style="padding:8px 0">No classes currently available for this age.</p>`}
          </div>`;
        }).join('')}
      </div>

      <div>
        <div class="card" style="position:sticky;top:24px">
          <div class="card-hd">Your selections</div>
          ${cartCount() ? `<div style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px">
            ${Object.entries(classCart).map(([childId, ids]) => ids.map(cid => {
              const k = kids.find(x => x.id === childId); const p = PROGRAMS.find(x => x.id === cid);
              if (!k || !p) return '';
              return `<div style="display:flex;justify-content:space-between;gap:10px;font-size:13px">
                <div><div style="color:var(--cream)">${esc(p.name)}</div><div style="font-size:11px;color:var(--muted)">${esc(k.name)}</div></div>
                <div style="color:var(--gold-light);flex-shrink:0">${fmt$(programOneTimePrice(p))}</div>
              </div>`;
            }).join('')).join('')}
          </div>
          <hr style="border:none;border-top:1px solid var(--border-soft);margin:0 0 14px">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
            <span style="font-size:14px;color:var(--cream)">Total due today</span>
            <span style="font-family:'Libre Baskerville',serif;font-size:26px;font-weight:700;color:var(--cream)">${fmt$(total)}</span>
          </div>
          ${card
            ? `<div style="font-size:12px;color:var(--muted);margin-bottom:14px">Charged to your card on file (${card})</div>
               <button class="btn" style="width:100%" data-action="checkout-classes">Enroll &amp; pay ${fmt$(total)}</button>`
            : `<div style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file. Add one in Billing first.</div>
               <button class="btn" style="width:100%" disabled>Enroll &amp; pay</button>`}
          <p style="font-size:11px;color:var(--muted);text-align:center;margin-top:10px;line-height:1.6">One-time charge. Classes and lessons are not billed monthly.</p>`
          : `<p class="summary-empty" style="text-align:center;padding:12px 0;font-style:italic;color:var(--muted)">Select classes to get started.</p>`}
        </div>
      </div>
    </div>`;
};

VIEW_INIT['p-classes'] = () => {
  document.querySelectorAll('[data-action="cart-toggle"]').forEach(cb => {
    cb.addEventListener('change', () => cartToggle(cb.dataset.kid, cb.dataset.program));
  });
  document.querySelectorAll('[data-action="checkout-classes"]').forEach(btn => {
    btn.addEventListener('click', checkoutClasses);
  });
};

function checkoutClasses() {
  if (!cartCount()) return;
  const total = cartTotal();
  const card = session.card ? ` to your ${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : '';
  openModal(`
    <h3>Confirm enrollment</h3>
    <p class="card-intro">Enroll in ${cartCount()} ${cartCount() === 1 ? 'class' : 'classes'} and charge <b style="color:var(--cream)">${fmt$(total)}</b>${card}? This is a one-time charge.</p>
    <div style="display:flex;gap:10px">
      <button class="btn" style="flex:1" id="classPayBtn">Confirm — charge ${fmt$(total)}</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('classPayBtn').addEventListener('click', processClassCheckout);
}

async function processClassCheckout() {
  const btn = document.getElementById('classPayBtn');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Processing…'; }
  try {
    for (const [childId, ids] of Object.entries(classCart)) {
      for (const cid of ids) {
        await apiFetch('/enrollments', {
          method: 'POST',
          body: JSON.stringify({ performerId: childId, programId: cid, seasonYear: currentSeasonYear(), billingMode: 'onetime' })
        });
      }
    }
    classCart = {};
    DB.charges = await apiFetch('/charges?accountId=' + session.id);
    closeModal(); refresh();
    toast('Enrolled! Your one-time charge has been processed.');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Confirm'; }
    showChargeError(e.message || 'The charge was declined. No classes were enrolled.');
  }
}
